import AvatarGroupM from "@common/components/avatar/AvatarGroupM"
import { useTutorNoShow } from "@common/hooks/livekit/tutorNoShow"
import { useDisconnectFromLive } from "../../engine/live/room/useDisconnectFromLive"
import { isolatedRoomTypes } from "@common/hooks/livekit/useFilteredTracks"
import { useIsSoundMode } from "@common/hooks/livekit/useParticipantState"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { useCameraStateRecorder } from "../../engine/live/room/cameraTelemetry"
import { MuteCurrentUser } from "../../engine/live/room/MuteCurrentUser"
import { useDisconnecting } from "@common/livekit/components/useDisconnecting"
import { onIsolatedAvatarPress } from "@common/livekit/onIsolatedAvatarPress"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { ConnectingView } from "@common/video-call/ConnectingView"
import DraggableView from "@common/video-call/DraggableView"
import PermissionsManager from "@common/video-call/PermissionsManager"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import {
    setFocusedMode,
    setIsLiveModeEnabled,
    setMiniMode,
} from "@data/redux/actions/liveMode"
import { setMuteVideo } from "@data/redux/actions/videoCall"
import { liveService } from "@data/services/liveService"
import { liveServiceV2 } from "@data/services/liveServiceV2"
import LiveEventBuffer from "@data/services/LiveEventBuffer"
import { useInitialCamRecord } from "../../engine/live/room/useInitialCamRecord"
import { useLiveKitWebReconnect } from "../../engine/live/room/useLiveKitWebReconnect"
import { hasRole } from "@data/utility/ability"
import {
    RoomAudioRenderer,
    useConnectionState,
    useRoomContext,
} from "@livekit/components-react"
import { useNavigation, useRoute } from "@react-navigation/native"
import { ConnectionState } from "livekit-client"
import React, { useContext, useEffect, useRef, useState } from "react"
import { useWindowDimensions, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { connect } from "react-redux"
import {
    selectInCallUsers,
    selectLiveParticipants,
    selectLiveTutors,
    selectRaisedHands,
} from "@data/redux/selectors/liveModeSelectors"
import ConnectionHeader from "../../student/activity/one-step/ConnectionHeader"
import GrayOutComponent from "../components/gray-out/GrayOutComponent"
import { CustomIconButton } from "../header/LiveModeFooter"
import RoomView from "./RoomView"
import {
    CameraPortalAction,
    decideCameraPortalAction,
} from "../../engine/live/room/cameraReleaseDecision"
import { ControlBarM } from "./components-web/ControlBarM"
import StudentControlBar from "./components-web/StudentControlBar"
import ConnectionQualityAlert from "./components/ConnectionQualityAlert"
import InformationBarComponent from "./components/InformationBarComponent"
import LiveWebMobileBanner from "@common/liveMode/LiveWebMobileBanner"
import LiveKitStatsMonitor from "./components-web/LiveKitStatsMonitor"
import { StartAudioOverlay } from "./components-web/StartAudioOverlay"

const LKVideoCallPortal = (props) => {
    const { users, suspended = false } = props
    const { width: windowWidth, height: windowHeight } = useWindowDimensions()
    const insets = useSafeAreaInsets()

    const miniMode = props.miniMode || false
    const setMiniMode = props.setMiniMode || (() => {})

    const [pinnedMode, setPinnedMode] = useState(true)
    const [initialLoad, setInitialLoad] = useState(false)

    // Access tutor no-show context for waiting banner
    const tutorNoShow = useTutorNoShow()

    const liveSession = props.liveSessions?.find(
        (x) => x?.id === props.liveSessionId
    )
    const tutor = liveSession?.users?.find((l) => l?.is_tutor)

    const room = useRoomContext()
    const { disconnectFromLive } = useDisconnectFromLive()
    const token = props.roomData?.token

    const connectionState = useConnectionState()

    const isConnected = connectionState === "connected"
    const disconnecting = useDisconnecting()

    const [isDenied, setIsDenied] = useState(true)
    const [permissionsChecked, setPermissionsChecked] = useState(false)

    const navigation = useNavigation()
    const route = useRoute()
    const isHandRaised = props.raisedHands?.includes(props.user?.id)

    const isSoundModeRaw = useIsSoundMode(props.user?.id)
    const isSoundMode = hasRole(props.user, "student") && isSoundModeRaw

    const isTutor = hasRole(props.user, "tutor")

    useInitialCamRecord(isConnected, room)

    // Dernier recours : sur web, aucun code ne relance une room arrêtée — voir
    // le docblock du hook. N'agit que sur `disconnected`, donc jamais pendant
    // une reprise du SDK.
    const webReconnect = useLiveKitWebReconnect(
        room,
        props.roomData,
        props.otherTabPresent
    )

    useEffect(() => {
        if (!isSoundMode && !miniMode && isConnected && initialLoad)
            setMiniMode(true)
    }, [isSoundMode])

    // Volume reconciliation removed: conditional rendering of RoomAudioRenderer
    // handles this structurally (no <audio> elements when in concentré mode).

    const tutorParticipants =
        liveSession?.users?.filter((user) => user?.is_tutor) ?? []
    const presentTutors =
        tutorParticipants?.filter((user) =>
            props.participants?.includes(user?.id)
        ) ?? []
    const isTutorPresent = useRef(presentTutors?.length > 0)
    const skipCleanupRef = useRef(false)

    useEffect(() => {
        isTutorPresent.current = presentTutors?.length > 0
    }, [presentTutors?.length])

    // Tutor no-show logic is now handled by TutorNoShowProvider in LiveContainerV3
    // Legacy tutor no-show dialog logic - now replaced by InitialDialog component
    // const showPendingDialogIfNoTutor = () => {
    //     // if there is no tutor connected, show a dialogm to explain to the student
    //     if (!isTutorPresent.current && hasRole(props.user, "student")) {
    //         DialogM.show({
    //             champion: "abou",
    //             variant: "main",
    //             text1: "Ton tuteur n'est pas connecté",
    //             text2:
    //                 props.user?.todo_activities?.length > 0
    //                     ? "En attendant qu'il arrive, travaille sur ton activité !"
    //                     : "En attendant qu'il arrive, tu peux commencer à faire tes devoirs",
    //             onPress: () => {
    //                 setMiniMode(true)
    //                 setFocusedMode(false)
    //             },
    //             onPressBack: () => {
    //                 setMiniMode(true)
    //                 setFocusedMode(false)
    //             },
    //         })
    //     }
    // }

    // Le retry de 6 s qui vivait dans cet effet est supprimé : il détruisait la
    // reprise de LiveKit. La reconnexion appartient au seul SDK — voir
    // `roomOptions.js`.
    useEffect(() => {
        if (isConnected && !initialLoad) {
            setInitialLoad(true)
            // Legacy timeout for showPendingDialogIfNoTutor - now handled by useTutorNoShowLogic
            // const endingDialogIfNoTutorTimeout = setTimeout(
            //     showPendingDialogIfNoTutor,
            //     6000
            // )
            // return () => clearTimeout(endingDialogIfNoTutorTimeout)
        }
    }, [isConnected, initialLoad])

    useEffect(() => {
        props.apiRequest(liveServiceV2.joinCallV2, { id: props.liveSessionId })

        // Initialize the event buffer for batched event syncing
        LiveEventBuffer.getInstance().init(props.liveSessionId, props.user?.id)

        return () => {
            if (!skipCleanupRef.current) {
                props.apiRequest(liveServiceV2.leaveCallV2, {
                    id: props.liveSessionId,
                })
            }
            // Destroy buffer (flushes remaining events to backend)
            LiveEventBuffer.getInstance().destroy()
            room.disconnect(true)
                .then()
                .catch((reason) => {
                    console.log("DISCONNECT ERROR LIVEKIT", reason)
                })
        }
    }, [])

    const dimensions = useContext(DimensionsContext)

    const disableFocusedMode = dimensions?.width >= 700

    const focusedMode = props.focusedMode || false

    const setFocusedMode = (value) => {
        if (disableFocusedMode) {
            return
        }
        props.setFocusedMode(value)
    }

    const isFocusedMode = focusedMode && !disableFocusedMode && !isTutor

    const bgColor = isTutor
        ? theme.colors.grey
        : isSoundMode
          ? theme.colors.success
          : theme.colors.purple

    const isSilentMode = !isSoundMode && !isTutor

    const smallScreenControlBarHeight = miniMode && windowWidth < 700 ? 70 : 0

    const safeHeight =
        windowHeight - insets.top - insets.bottom - smallScreenControlBarHeight
    const safeWidth = windowWidth - insets.left - insets.right

    const miniHeight = isTutor
        ? Math.min(
              (windowHeight * 2) / 3,
              Math.max(70 + props.participants?.length * 200, 270)
          )
        : 270

    const miniWidth = isFocusedMode
        ? miniHeight
        : Math.max(windowWidth / 4, 150)

    const contentHeight = miniMode
        ? miniHeight * (isSilentMode ? 2 / 3 : 1)
        : safeHeight
    const contentWidth = miniMode
        ? miniWidth * (isSilentMode ? 2 / 3 : 1)
        : safeWidth

    const initialX = safeWidth - contentWidth - (miniMode ? 5 : 0)
    const initialY = safeHeight - contentHeight - (miniMode ? 5 : 0)

    const localParticipant = room?.localParticipant

    useEffect(() => {
        if (!isConnected) return () => {}

        const action = decideCameraPortalAction({
            cameraOn: props.activityCameraOn,
            reusesLiveTrack: props.cameraReusesLiveTrack,
            isCameraEnabled: localParticipant?.isCameraEnabled,
            isVideoMuted: props.isVideoMuted,
        })

        if (action === CameraPortalAction.RELEASE_CAMERA) {
            props.setMuteVideo(false)
            localParticipant
                .setCameraEnabled(false)
                .then()
                .catch((reason) => {
                    console.log("ERROR SETTING CAMERA", reason)
                })
        } else if (action === CameraPortalAction.MUTE_ONLY) {
            props.setMuteVideo(true)
        } else if (action === CameraPortalAction.RESTORE_CAMERA) {
            localParticipant
                .setCameraEnabled(true)
                .then()
                .catch((reason) => {
                    console.log("ERROR SETTING CAMERA", reason)
                })
        }
    }, [props.activityCameraOn, props.cameraReusesLiveTrack])

    useEffect(() => {
        if (miniMode) {
            if (!pinnedMode) setPinnedMode(true)
        }
    }, [miniMode])

    useEffect(() => {
        if (props.activityPaused) {
            if (!miniMode) setMiniMode(true)
        }
    }, [props.activityPaused])

    const debouncedOnOffCam = useCameraStateRecorder()

    useEffect(() => {
        if (props.isAgentModalVisible) {
            setMiniMode(true)
            setFocusedMode(false)
        }
    }, [props.isAgentModalVisible])

    useApiStatus(liveService.leaveLiveSession, false, true, () => {
        disconnectFromLive()
        props.apiIdle(liveService.sendSatisfaction)
        props.apiIdle(liveService.sendStudentComment)
    })

    const liveStudents = liveSession?.users?.filter(
        (user) => !user?.is_tutor && props.participants?.includes(user?.id)
    )

    return (
        <>
            <GrayOutComponent
                visible={isFocusedMode}
                onPress={() => {
                    setFocusedMode(false)
                }}
            />
            <DraggableView
                initValue={{
                    x: initialX,
                    y: initialY,
                }}
                isDraggable={miniMode}
                style={{
                    position: "absolute",
                    marginTop: insets.top,
                    marginBottom: insets.bottom,
                    marginLeft: insets.left,
                    marginRight: insets.right,
                    backgroundColor: theme.colors.grey,
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: suspended ? 0 : 1,
                    overflow: "hidden",
                    zIndex: 10,
                    elevation: 10,
                    ...(miniMode && { borderRadius: 16 }),
                }}
                contentHeight={suspended ? 0 : contentHeight}
                contentWidth={suspended ? 0 : contentWidth}
                maxHeight={safeHeight}
                maxWidth={safeWidth}
            >
                <View
                    style={{
                        width: contentWidth,
                        height: contentHeight,
                    }}
                >
                    {/* Rappel permanent en web mobile : l'appli est bien plus stable
                        que le navigateur du téléphone. Il vit dans le portal, qui
                        recouvre l'écran de l'élève sur web, sinon il serait masqué. */}
                    {!miniMode && <LiveWebMobileBanner />}
                    {!miniMode && (
                        <InformationBarComponent
                            isSilentMode={!isSoundMode && !isTutor}
                            isHandRaised={isHandRaised}
                            isWaitingForTutor={tutorNoShow?.shouldShowBanner}
                            waitingMessage={tutorNoShow?.waitingMessage}
                            bgColor={
                                tutorNoShow?.shouldShowBanner
                                    ? theme.colors.warning
                                    : bgColor
                            }
                        />
                    )}
                    <View style={{ flex: 1 }}>
                        {isConnected && !!token && permissionsChecked && (
                            <View
                                style={{
                                    flex: 1,
                                    backgroundColor: "black",
                                    ...(miniMode && { borderRadius: 16 }),
                                }}
                            >
                                {/* Mute local mic when soundMode is off */}
                                <MuteCurrentUser
                                    isSilentMode={!isSoundMode && !isTutor}
                                />

                                {/* Your custom component with basic video conferencing functionality. */}
                                <View style={{ flex: 1 }}>
                                    <RoomView
                                        participants={users}
                                        pinnedMode={pinnedMode}
                                        setPinnedMode={setPinnedMode}
                                        miniMode={miniMode}
                                        setMiniMode={setMiniMode}
                                        contentWidth={contentWidth}
                                        contentHeight={contentHeight}
                                        currentUser={props.user}
                                        tutor={tutor}
                                        focusedMode={isFocusedMode}
                                        setFocusedMode={setFocusedMode}
                                        isTutor={isTutor}
                                        isSoundMode={isSoundMode}
                                        onOffCam={debouncedOnOffCam}
                                        isHandRaised={isHandRaised}
                                        nav={navigation}
                                        route={route}
                                    />
                                    {!miniMode &&
                                        isolatedRoomTypes.includes(
                                            liveSession?.live_room?.type
                                        ) &&
                                        liveStudents?.length > 1 && (
                                            <AvatarGroupM
                                                users={liveStudents}
                                                style={{
                                                    position: "absolute",
                                                    top: 15,
                                                    left: 15,
                                                    paddingVertical: 6,
                                                    paddingHorizontal: 10,
                                                    backgroundColor:
                                                        theme.colors.lighter,
                                                    borderRadius: 15,
                                                    zIndex: 9999,
                                                }}
                                                size={32}
                                                onPress={() => {
                                                    onIsolatedAvatarPress(
                                                        liveStudents
                                                    )
                                                }}
                                            />
                                        )}
                                    {!miniMode && <ConnectionHeader />}
                                </View>
                                {/* The RoomAudioRenderer takes care of room-wide audio for you. */}
                                {/* Conditionally rendered: no <audio> elements in concentré = structural silence */}
                                {(isSoundMode || isTutor) && (
                                    <RoomAudioRenderer volume={1} />
                                )}

                                {/* Student-only: tap-to-resume when the browser blocks autoplay */}
                                {!isTutor && <StartAudioOverlay />}

                                {/* Stats monitor for dev - logs to console */}
                                {/* {__DEV__ && <LiveKitStatsMonitor />} */}

                                <View
                                    style={{
                                        backgroundColor:
                                            theme.colors.blackAlmost,
                                        width: "100%",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    {isTutor ? (
                                        <ControlBarM
                                            miniMode={miniMode}
                                            pinnedMode={pinnedMode}
                                            setPinnedMode={setPinnedMode}
                                            isSoundMode={isSoundMode || isTutor}
                                            onOffCam={debouncedOnOffCam}
                                            width={contentWidth}
                                            skipAudioRenderer
                                        />
                                    ) : (
                                        !miniMode && (
                                            <StudentControlBar
                                                miniMode={miniMode}
                                                setMiniMode={setMiniMode}
                                                setFocusedMode={setFocusedMode}
                                                isSoundMode={isSoundMode}
                                                onOffCam={debouncedOnOffCam}
                                                bgColor={
                                                    theme.colors.blackAlmost
                                                }
                                                nav={navigation}
                                                route={route}
                                            />
                                        )
                                    )}
                                </View>

                                {/* Controls for the user to start/stop audio, video, and screen share tracks and to leave the room. */}
                                <ConnectionQualityAlert miniMode={miniMode} />
                            </View>
                        )}

                        <View
                            style={{
                                bottom: null,
                                position: "absolute",
                                right: 10,
                                top: 10,
                                justifyContent: "flex-end",
                            }}
                        >
                            {(!miniMode ||
                                isFocusedMode ||
                                disableFocusedMode) && (
                                <CustomIconButton
                                    icon={
                                        miniMode ? "expand-alt" : "compress-alt"
                                    }
                                    iconColor={theme.colors.primary}
                                    containerColor={theme.colors.lighter}
                                    disabled={false}
                                    onPress={() => {
                                        if (miniMode) {
                                            setMiniMode(false)
                                        } else {
                                            setMiniMode(true)
                                            setFocusedMode(false)
                                        }
                                    }}
                                />
                            )}
                        </View>
                    </View>
                </View>
                {(!isConnected || !token) && (
                    <ConnectingView
                        width={contentWidth}
                        height={contentHeight}
                        onRetry={webReconnect.onRetry}
                        isRetrying={webReconnect.isRetrying}
                        text={
                            connectionState?.toString() ===
                            ConnectionState.Reconnecting
                                ? "Reconnexion..."
                                : disconnecting
                                  ? "Déconnexion..."
                                  : "Connexion..."
                        }
                        onPress={() => {
                            props.apiRequest(liveService.leaveLiveSession, {
                                id: props.liveSessionId,
                            })
                        }}
                    />
                )}
            </DraggableView>
            <PermissionsManager
                isDenied={isDenied}
                setIsDenied={setIsDenied}
                permissionsChecked={permissionsChecked}
                setPermissionsChecked={setPermissionsChecked}
            />
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        miniMode: state.liveMode?.miniMode,
        focusedMode: state.liveMode?.focusedMode,
        participants: selectLiveParticipants(state),
        tutors: selectLiveTutors(state),
        liveSessions: state.data.currentUser?.object?.live_sessions,
        liveSessionId: state.liveMode?.liveSessionId,
        inCallUsers: selectInCallUsers(state),
        roomData: state.liveKitRoom?.roomData,
        isVideoMuted: state.liveMode?.isVideoMuted,
        activityCameraOn: state.activity?.cameraOn,
        cameraReusesLiveTrack: state.activity?.cameraReusesLiveTrack,
        leaveLiveSessionRequest: state.api.leaveLiveSession?.request,
        activityPaused: state.activity?.activityPaused,
        timesSinceLastCall: state.liveMode?.timesSinceLastCall,
        cameraState: state?.camera,
        raisedHands: selectRaisedHands(state),
        isAgentModalVisible: state.liveMode?.isAgentModalVisible,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    setIsLiveModeEnabled,
    setMuteVideo,
    setMiniMode,
    setFocusedMode,
}
export default connect(mapStateToProps, mapDispatchToProps)(LKVideoCallPortal)
